import { isEduPortal, isPathbuddyHost } from './eduHost';
import { EDU_CANONICAL_BASE, EDU_HOME_URL, stripAppBasename } from './appBase';
import { EXAM_PACKAGES } from './pricingCatalog';
import { normalizePath, readHostRedirectMap, resolveHostRedirect } from '../features/marketing/hostRedirects';
import { loadArticle } from '../features/marketing/insights/articleLoaders';
import eduCopy from './eduMarketingCopy.json';

const EIGHT_EXAMS = [
  { slug: 'degree-apprenticeships', needle: 'degreeapprenticeship' },
  { slug: 'medicine', needle: 'medicalschool' },
  { slug: 'oxbridge', needle: 'oxbridge' },
  { slug: 'lnat', needle: 'lnat' },
  { slug: 'tmua', needle: 'tmua' },
  { slug: 'ucat', needle: 'ucat' },
  { slug: 'esat', needle: 'esat' },
  { slug: 'tara', needle: 'tara' },
];

const CATALOG_ID = {
  '/lnat': 'lnat',
  '/tmua': 'tmua',
  '/ucat': 'ucat',
  '/esat': 'esat',
  '/tara': 'tara',
  '/oxbridge': 'oxbridge',
  '/medicine': 'medicalschool',
  '/degree-apprenticeships': 'degree_apprenticeships',
};

const articleCache = new Map();
const articlePending = new Map();

/**
 * Does this page use the edu marketing title, description and canonical?
 * Yes on the edu hosts and on ivoriai.com under /edu; no on pathbuddy.com and
 * on the company home. browserPath is the full window path (defaults to the
 * page that is loaded), because the router-relative path cannot tell
 * ivoriai.com/ from ivoriai.com/edu.
 */
export function usesEduMarketingMeta(hostname, browserPath) {
  if (isPathbuddyHost(hostname)) return false;
  const h = (hostname || '').toLowerCase();
  return isEduPortal(h, browserPath) || eightExamFromHost(h) != null;
}

export function eduMarketingTitle(pathname) {
  const path = normalizePath(pathname);
  return eduCopy.titles[path] || null;
}

export function isEduMarketingPath(pathname) {
  return Boolean(eduCopy.titles[normalizePath(pathname)]);
}

function articleParts(pathname) {
  const path = normalizePath(pathname);
  const match = path.match(/^\/insights\/([^/]+)\/([^/]+)$/);
  if (!match) return null;
  return { path, exam: match[1], slug: match[2] };
}

export function firstParagraph(data) {
  const blocks = data && Array.isArray(data.blocks) ? data.blocks : [];
  for (let i = 0; i < blocks.length; i += 1) {
    const block = blocks[i];
    if (block && block.t === 'p' && typeof block.x === 'string' && block.x.trim()) {
      return block.x.trim();
    }
  }
  return '';
}

function publishArticleDescription(path, description) {
  if (typeof document === 'undefined' || !description) return;
  // path is router-relative; the browser path carries the /edu prefix.
  if (normalizePath(stripAppBasename(window.location.pathname)) !== path) return;
  const descriptionMeta = document.querySelector('meta[name="description"]');
  if (descriptionMeta) descriptionMeta.setAttribute('content', description);
  const og = document.querySelector('meta[property="og:description"]');
  if (og) og.setAttribute('content', description);
  const twitter = document.querySelector('meta[property="twitter:description"]');
  if (twitter) twitter.setAttribute('content', description);
  const script = document.querySelector('#seo-meta-jsonld');
  if (!script || !script.textContent) return;
  try {
    const data = JSON.parse(script.textContent);
    data.description = description;
    script.textContent = JSON.stringify(data);
  } catch {
    return;
  }
}

function rememberArticle(path, exam, slug) {
  if (articleCache.has(path) || articlePending.has(path)) return;
  const pending = loadArticle(exam, slug).then((mod) => {
    const text = firstParagraph(mod && (mod.default || mod));
    articleCache.set(path, text);
    articlePending.delete(path);
    publishArticleDescription(path, text);
    return text;
  }).catch(() => {
    articleCache.set(path, '');
    articlePending.delete(path);
    return '';
  });
  articlePending.set(path, pending);
}

/**
 * Sync description for a §2.1 path. Article copy is the article's first
 * paragraph; until that chunk loads the description is empty, then the
 * meta tag is updated. Other pages use the lede already on the page.
 * Returns null when the path is not a marketing path.
 */
export function eduMarketingDescription(pathname) {
  const path = normalizePath(pathname);
  if (!eduCopy.titles[path]) return null;
  if (Object.prototype.hasOwnProperty.call(eduCopy.descriptions, path)) {
    return eduCopy.descriptions[path];
  }
  const article = articleParts(path);
  if (!article) return null;
  if (articleCache.has(path)) return articleCache.get(path);
  rememberArticle(path, article.exam, article.slug);
  return '';
}

export function whenEduArticleDescription(pathname) {
  const article = articleParts(pathname);
  if (!article) return Promise.resolve(null);
  if (articleCache.has(article.path)) return Promise.resolve(articleCache.get(article.path));
  rememberArticle(article.path, article.exam, article.slug);
  return articlePending.get(article.path) || Promise.resolve('');
}

export function catalogPrice(price) {
  if (typeof price !== 'string') return null;
  const match = price.match(/^£(\d+)(?:\.(\d+))?$/);
  if (!match) return null;
  return match[2] ? `${match[1]}.${match[2]}` : match[1];
}

function offerFromPlan(plan, includeName) {
  const price = catalogPrice(plan && plan.price);
  if (price == null) return null;
  const offer = {
    '@type': 'Offer',
    price,
    priceCurrency: 'GBP',
    availability: 'https://schema.org/InStock',
  };
  if (includeName && plan.name) offer.name = plan.name;
  return offer;
}

/**
 * Offers only for pages that list prices. Amounts are parsed from
 * pricingCatalog.js. A price that is not a catalog £ amount is omitted.
 */
export function offersForMarketingPath(pathname) {
  const path = normalizePath(pathname);
  if (path === '/') {
    const seen = new Set();
    const offers = [];
    EXAM_PACKAGES.forEach((pkg) => {
      (pkg.plans || []).forEach((plan) => {
        const offer = offerFromPlan(plan, false);
        if (!offer || seen.has(offer.price)) return;
        seen.add(offer.price);
        offers.push(offer);
      });
    });
    return offers;
  }
  if (path === '/pricing') {
    const offers = [];
    EXAM_PACKAGES.forEach((pkg) => {
      (pkg.plans || []).forEach((plan) => {
        const offer = offerFromPlan(plan, true);
        if (offer) offers.push(offer);
      });
    });
    return offers;
  }
  const catalogId = CATALOG_ID[path];
  if (!catalogId) return [];
  const pkg = EXAM_PACKAGES.find((item) => item.id === catalogId);
  if (!pkg) return [];
  return (pkg.plans || []).map((plan) => offerFromPlan(plan, true)).filter(Boolean);
}

function eightExamFromHost(hostname) {
  const h = (hostname || '').toLowerCase();
  for (let i = 0; i < EIGHT_EXAMS.length; i += 1) {
    if (h.indexOf(EIGHT_EXAMS[i].needle) !== -1) return EIGHT_EXAMS[i];
  }
  return null;
}

function redirectMap() {
  if (typeof document === 'undefined') return null;
  return readHostRedirectMap(document);
}

function redirectedMarketingUrl(hostname, pathname) {
  const map = redirectMap();
  if (!map) return null;
  return resolveHostRedirect(hostname, pathname, map);
}

/**
 * Canonical for edu marketing paths is https://ivoriai.com/edu<path>; the
 * edu home is https://ivoriai.com/edu (no trailing slash).
 * Exam-subdomain URLs for the eight redirected exams become that edu path.
 * pathbuddy.com is not rewritten here. pathname is router-relative (no /edu).
 */
export function resolveEduCanonical({ pathname, hostname, requestedUrl, href, browserPath }) {
  const path = normalizePath(pathname);
  const currentHref = String(href || '').split('#')[0];
  if (isPathbuddyHost(hostname)) {
    return requestedUrl || currentHref;
  }

  if (eightExamFromHost(hostname)) {
    const redirected = redirectedMarketingUrl(hostname, path);
    if (redirected) return redirected;
  }

  if (usesEduMarketingMeta(hostname, browserPath) && isEduMarketingPath(path)) {
    return path === '/' ? EDU_HOME_URL : `${EDU_CANONICAL_BASE}${path}`;
  }

  if (requestedUrl) {
    try {
      const requested = new URL(requestedUrl);
      if (eightExamFromHost(requested.hostname)) {
        const redirected = redirectedMarketingUrl(requested.hostname, requested.pathname);
        if (redirected) return redirected;
      }
    } catch {
      return requestedUrl;
    }
    return requestedUrl;
  }

  return currentHref;
}

export function buildEduJsonLd({ title, description, url, pathname }) {
  const offers = offersForMarketingPath(pathname);
  const data = {
    '@context': 'https://schema.org',
    '@type': offers.length ? 'Product' : 'WebPage',
    name: title,
    description,
    url,
    provider: {
      '@type': 'Organization',
      name: 'ivori AI',
      sameAs: 'https://ivoriai.com',
    },
  };
  if (offers.length) data.offers = offers;
  return data;
}

export function applyEduDocumentSeo(pathname, hostname, title, description) {
  if (typeof document === 'undefined') return;
  if (!usesEduMarketingMeta(hostname) || !isEduMarketingPath(pathname)) return;
  const canonicalUrl = resolveEduCanonical({
    pathname,
    hostname,
    href: window.location.href,
  });
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', canonicalUrl);

  let schemaScript = document.querySelector('#seo-meta-jsonld');
  if (!schemaScript) {
    schemaScript = document.createElement('script');
    schemaScript.setAttribute('id', 'seo-meta-jsonld');
    schemaScript.setAttribute('type', 'application/ld+json');
    document.head.appendChild(schemaScript);
  }
  schemaScript.textContent = JSON.stringify(buildEduJsonLd({
    title,
    description,
    url: canonicalUrl,
    pathname,
  }));
}
